import React from 'react';
import Banner from './Banner';
import type { UncoveredRange } from './creativeCoverage';

export interface CoverageBannerSlot {
  id: string;
  filename: string;
  startDate: string;
  endDate: string;
}

interface CoverageBannerProps {
  uncoveredRanges: UncoveredRange[];
  surface: 'wizard' | 'swap' | 'extend';
  // Extra body line — used by Edit Commercial to flag pre-existing gaps that
  // existed when the modal opened, and by Extend to point the buyer to Edit
  // commercial when no date drag will cover the new window.
  prefix?: React.ReactNode;
  className?: string;
  // CTV-506: when present, the banner renders per-gap recommendation buttons
  // that extend the bordering commercial through the gap. Wired by the wizard
  // (onSlotChange) and Edit Commercial (patchSlot), both of which re-derive
  // segment shares and re-check coverage on date change.
  slots?: CoverageBannerSlot[];
  onExtendLeftCreative?: (creativeId: string, newEndDate: string) => void;
  onExtendRightCreative?: (creativeId: string, newStartDate: string) => void;
}

const COPY: Record<CoverageBannerProps['surface'], string> = {
  wizard:
    "Your commercials don't run on every day of your campaign. Add a commercial for the dates below, or extend an existing commercial to cover them.",
  swap:
    "Your commercials don't run on every day of your campaign. Add a commercial for the dates below, or extend an existing commercial to cover them.",
  extend:
    "Your commercials don't cover the extended dates. Drag a commercial's end date forward, or add a new commercial in the Edit commercial tab and come back.",
};

export const CoverageBanner: React.FC<CoverageBannerProps> = ({
  uncoveredRanges,
  surface,
  prefix,
  className,
  slots,
  onExtendLeftCreative,
  onExtendRightCreative,
}) => {
  if (uncoveredRanges.length === 0) return null;
  return (
    <Banner tone="caution" label="Coverage gap" className={className}>
      {prefix && <p className="mb-1">{prefix}</p>}
      <p>{COPY[surface]}</p>
      <ul className="mt-1 list-disc pl-4 space-y-1">
        {uncoveredRanges.map((r) => {
          const left = slots ? pickLeftBorder(slots, r) : null;
          const right = slots ? pickRightBorder(slots, r) : null;
          return (
            <li key={`${r.startDate}-${r.endDate}`}>
              <div>{formatRange(r)}</div>
              {(left || right) && (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {left && onExtendLeftCreative && (
                    <RecommendationButton
                      label={`Extend ${left.filename || 'commercial'} through ${formatShort(r.endDate)}`}
                      onClick={() => onExtendLeftCreative(left.id, r.endDate)}
                    />
                  )}
                  {right && onExtendRightCreative && (
                    <RecommendationButton
                      label={`Extend ${right.filename || 'commercial'} back to ${formatShort(r.startDate)}`}
                      onClick={() => onExtendRightCreative(right.id, r.startDate)}
                    />
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Banner>
  );
};

// CTV-506: matches the outlined-pill pattern from InventoryWarningCard's
// `GeoButton` (rounded-full, white fill, tonal border + text, subtle shadow,
// active scale on tap). Swapped from orange to amber to coexist with this
// banner's caution tone palette.
const RecommendationButton: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-300 px-3 py-1.5 text-[12px] font-semibold text-amber-700 shadow-sm transition-colors hover:bg-amber-100 active:scale-[0.98]"
  >
    {label}
  </button>
);

function pickLeftBorder(
  slots: CoverageBannerSlot[],
  gap: UncoveredRange,
): CoverageBannerSlot | null {
  const candidates = slots.filter((s) => s.endDate < gap.startDate);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    if (b.endDate !== a.endDate) return b.endDate < a.endDate ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
  return candidates[0];
}

function pickRightBorder(
  slots: CoverageBannerSlot[],
  gap: UncoveredRange,
): CoverageBannerSlot | null {
  const candidates = slots.filter((s) => s.startDate > gap.endDate);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    if (a.startDate !== b.startDate) return a.startDate < b.startDate ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
  return candidates[0];
}

function formatShort(iso: string): string {
  const d = parseIso(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatRange(r: UncoveredRange): string {
  const start = parseIso(r.startDate);
  const end = parseIso(r.endDate);
  if (!start || !end) return `${r.startDate} – ${r.endDate}`;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  if (r.startDate === r.endDate) return `${formatDate(start)} (1 day)`;
  return `${formatDate(start)} – ${formatDate(end)} (${days} days)`;
}

function parseIso(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default CoverageBanner;
